/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef, useLayoutEffect } from 'react'
import propTypes from 'prop-types'
import FaIcon from '../FaIcon/FaIcon'
import * as changeCase from 'change-case'
import { gsap } from 'gsap'
import { useGSAP } from '@gsap/react'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { ScrollToPlugin } from 'gsap/ScrollToPlugin'
import { useBpListener } from '../../../utils/Breakpoints/Breakpoints'
import { useOutsideClick } from '../../../hooks/useOutsideClick'
import Divider from '../Divider/Divider'
import Menu from '../Menu/Menu'
import { useLocation } from 'react-router-dom'

import v from '../../../styles/modules/_variables.scss'
import './SubNav.scss'

const SubNav = ({
  links,
  inline,
  pinNav,
  togglePinNav,
  navOpen,
  toggleNavOpen,
  setNavOpen,
  sizing,
}) => {
  // Import Sass variable(s)
  const {
    'nav-spacing-xxs': navSpacingXxs,
    'nav-spacing-xs': navSpacingXs,
    'nav-spacing-sm': navSpacingSm,
    'h-spacing-xxs': hSpacingXxs,
    'h-spacing-xs': hSpacingXs,
    'h-spacing-sm': hSpacingSm,
    'h-spacing-md': hSpacingMd,
    'header-height-sm': headerHeightSm,
    'header-height-md': headerHeightMd,
    borderRadius: brDefault,
  } = v

  const hSpacing = {
    xxs: hSpacingXxs,
    xs: hSpacingXs,
    sm: hSpacingSm,
    md: hSpacingMd,
  }

  const navSpacing = {
    xxs: navSpacingXxs,
    xs: navSpacingXs,
    sm: navSpacingSm,
  }
  const currentHSpacing = hSpacing[useBpListener()]
  const currentNavSpacing = navSpacing[useBpListener()]
  const currentBp = useBpListener()
  const { belowXs, belowMd } = sizing

  const location = useLocation()

  gsap.registerPlugin(ScrollTrigger, ScrollToPlugin, useGSAP)

  const [iconStyle, setIconStyle] = useState('regular')

  useEffect(() => {
    const handleStyle = new Promise((resolve) => {
      setTimeout(() => {
        resolve(pinNav ? 'solid' : 'regular')
      }, 100)
    })
    handleStyle.then((res) => setIconStyle(res))

    // Close menu when unpinned
    if (!pinNav && navOpen) {
      setNavOpen(false)
    }
  }, [pinNav, navOpen, setNavOpen])

  const tlMenu = useRef()
  const duration = 0.15

  const [headerHeight, setHeaderHeight] = useState()
  useEffect(() => {
    const toNum = (h) => h && h.slice(0, -3) * 10
    setHeaderHeight(belowXs ? toNum(headerHeightSm) : toNum(headerHeightMd))
  }, [belowXs, headerHeightMd, headerHeightSm])

  const CreateTrigger = React.useCallback(() => {
    if (belowMd && pinNav && headerHeight) {
      tlMenu.current = gsap
        .timeline({ delay: 0, defaults: { duration: duration } })
        .fromTo(
          '.sub-nav',
          {
            borderWidth: 1,
            borderRadius: brDefault,
            marginLeft: 0,
            width: '100%',
          },
          {
            borderWidth: `0 0 1`,
            borderRadius: 0,
            marginLeft: `calc(${currentHSpacing}*-1)`,
            width: `calc(100% + ${currentHSpacing} * 2)`,
          }
        )
        .fromTo(
          '.sub-nav .inner-container',
          { width: '100%' },
          {
            width: `calc(100% - (${currentHSpacing} * 2))`,
          },
          '<'
        )
        .to(
          '.sub-nav .inner-container button.contents',
          { padding: `0 ${currentNavSpacing}` },
          '<'
        )
        .to(
          '.sub-nav .inner-container button.contents',
          { borderRadius: 0 },
          '<'
        )

      ScrollTrigger.create({
        trigger: '.sub-nav',
        animation: tlMenu.current,
        toggleActions: 'play none reset reverse',
        start: `top ${headerHeight}`,
        end: `bottom-=${headerHeight} top+=${headerHeight * 2}`,
        endTrigger: '.section-container',
        toggleClass: 'st-active',
        onLeave: () => {
          setNavOpen(false)
          gsap.to('.sub-nav', {
            opacity: 0,
            duration: duration,
          })
        },
        onEnterBack: () => {
          tlMenu.current.play()
          gsap.to('.sub-nav', {
            opacity: 1,
            duration: duration,
          })
        },
        onLeaveBack: () => {
          gsap.to('.sub-nav', {
            opacity: 1,
            duration: duration,
          })
        },
        onEnter: () => {
          gsap.to('.sub-nav', {
            opacity: 1,
            duration: duration,
          })
        },
      })
    }
  }, [
    belowMd,
    pinNav,
    headerHeight,
    brDefault,
    currentHSpacing,
    currentNavSpacing,
    duration,
    setNavOpen,
  ])

  useEffect(() => {
    const sectionContainer = document.querySelector('.section-container')
    let resizeObserver
    if (sectionContainer) {
      resizeObserver = new ResizeObserver(() => {
        ScrollTrigger.refresh()
      })
      resizeObserver.observe(sectionContainer)
    }

    const timeout = setTimeout(() => {
      ScrollTrigger.refresh()
    }, 500)

    return () => {
      if (resizeObserver) resizeObserver.disconnect()
      clearTimeout(timeout)
    }
  }, [location.pathname])

  useLayoutEffect(() => {
    CreateTrigger()
  }, [CreateTrigger])

  useEffect(() => {
    gsap.set('.sub-nav', {
      opacity: 1,
    })
    ScrollTrigger.killAll()
    CreateTrigger()
  }, [location.pathname, CreateTrigger])

  // Animate chevron icon
  useGSAP(
    () => {
      let tl = gsap.timeline({
        delay: 0,
        defaults: { duration: duration, ease: 'ease-out' },
      })

      belowMd &&
        (navOpen
          ? tl.to('.button-group .contents > .fa', {
              rotation: 90,
            })
          : tl.to('.button-group .contents > .fa', {
              rotation: 0,
            }))
    },
    { dependencies: [navOpen], revertOnUpdate: true }
  )

  const behavior = inline ? 'inline' : 'right-col'
  const pinned = pinNav && belowMd ? 'pinned' : ''
  const contentsOnClick = belowMd && { onClick: toggleNavOpen }

  const handleClickOutside = () => {
    navOpen && toggleNavOpen()
  }
  const subNavRef = useOutsideClick(
    (e, ref) =>
      ref.current &&
      !ref.current.contains(e.target) &&
      !e.target.matches('.sub-nav'),
    handleClickOutside
  )

  return (
    <nav className={`sub-nav ${behavior} ${pinned}`} ref={subNavRef}>
      <div className='inner-container'>
        {belowMd ? (
          <div className='button-group'>
            <button
              className={`contents ${belowMd ? 'collapsible' : 'static'}`}
              {...contentsOnClick}>
              {belowMd && <FaIcon size={12} name='chevron-right' />}
              <span>Contents</span>
            </button>
            {belowMd && <Divider type='vertical' offset={8} />}
            {inline && (
              <button className='pin' onClick={togglePinNav}>
                <FaIcon
                  style={iconStyle}
                  size={pinNav ? 16 : 14}
                  name='thumbtack'
                  className={pinNav ? 'active' : ''}
                />
              </button>
            )}
          </div>
        ) : (
          <div className='contents'>
            <span>Contents</span>
          </div>
        )}
        <Menu
          trigger={{ name: 'subNav', value: navOpen }}
          scope={{ ref: subNavRef, class: '.sub-nav' }}>
          <div className='link-group'>
            {links.map((link, i) => {
              return (
                <button
                  key={link}
                  className={`link ${changeCase.kebabCase(link)} ${
                    i === 0 ? 'active' : ''
                  }`}>
                  {link}
                </button>
              )
            })}
          </div>
          <Divider type='horizontal' offset={belowMd ? 0 : 16} />
          <button className='link go-to-top'>
            <div className='container'>
              <FaIcon size={14} name='arrow-up' />
              Top
            </div>
          </button>
        </Menu>
      </div>
    </nav>
  )
}

SubNav.propTypes = {
  links: propTypes.array.isRequired,
  inline: propTypes.bool,
  pinNav: propTypes.bool,
  togglePinNav: propTypes.func,
  navOpen: propTypes.bool,
  sizing: propTypes.shape(),
  toggleNavOpen: propTypes.func,
  setNavOpen: propTypes.func,
}

export default SubNav
